import { useState, useRef, useEffect } from 'react'
import { useEducatorProjectSubmissionChannel } from '@contexts/educatorProjectSubmissionChannelContext'
import { useFlashMessage } from '@components/FlashMessage'

const InstantFeedbackProvider = ({ children, project, submission }) => {
  const { setFlashMessage } = useFlashMessage()
  const { addListener } = useEducatorProjectSubmissionChannel()
  const [feedbackState, setFeedbackState] = useState({})
  const feedbackCleanupRef = useRef(null)

  const getNumericId = (gqlId) => {
    if (!gqlId) return null
    try {
      const decoded = atob(gqlId)
      const parts = decoded.split('/')
      return parseInt(parts[parts.length - 1], 10)
    } catch {
      return null
    }
  }

  const setupFeedbackSubscription = (blockId, blockSubmissionId) => {
    if (feedbackCleanupRef.current) {
      feedbackCleanupRef.current()
      feedbackCleanupRef.current = null
    }

    const numericBlockSubmissionId = getNumericId(blockSubmissionId)

    feedbackCleanupRef.current = addListener(data => {
      if (data.type === 'feedback_ready' && data.block_submission_id === numericBlockSubmissionId) {
        setFeedbackState(prev => ({
          ...prev,
          [blockId]: {
            ...prev[blockId],
            isWaitingForFeedback: false,
            instantFeedbackContent: {
              answer: data.answer,
              marks: data.marks,
              feedback: data.feedback,
              automarkedResult: data.automarked_result,
              attachments: data.attachments || []
            }
          }
        }))
        setFlashMessage('AI feedback ready! 🎉', 'success')
        feedbackCleanupRef.current?.()
        feedbackCleanupRef.current = null
      } else if (data.type === 'error') {
        setFlashMessage(data.message || 'Error receiving instant feedback.', 'error')
        setFeedbackState(prev => ({
          ...prev,
          [blockId]: {
            ...prev[blockId],
            isWaitingForFeedback: false
          }
        }))
        feedbackCleanupRef.current?.()
        feedbackCleanupRef.current = null
      }
    })
  }

  const setBlockFeedbackLoading = (blockId, blockSubmissionId) => {
    setFeedbackState(prev => ({
      ...prev,
      [blockId]: {
        ...prev[blockId],
        isWaitingForFeedback: true,
        instantFeedbackContent: null
      }
    }))
    setupFeedbackSubscription(blockId, blockSubmissionId)
  }

  const getBlockFeedbackState = (blockId) => {
    return feedbackState[blockId] || {
      isWaitingForFeedback: false,
      instantFeedbackContent: null
    }
  }

  useEffect(() => () => feedbackCleanupRef.current?.(), [])

  return children({
    setBlockFeedbackLoading,
    getBlockFeedbackState
  })
}

export default InstantFeedbackProvider 
